/**
 * Push Notification Prompt Component
 * Shows a banner to enable push notifications for the first time
 */
import React, { useState, useEffect } from 'react';
import { Bell, BellOff, X, CheckCircle } from 'lucide-react';
import { 
  isPushSupported, 
  getNotificationPermission, 
  setupPushNotifications 
} from '../utils/pushNotifications';
import { getToken } from '../utils/api';

export default function PushNotificationPrompt({ onDismiss }) {
  const [status, setStatus] = useState('idle'); // idle, requesting, success, error, denied
  const [errorMessage, setErrorMessage] = useState('');
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    // Check if we should show the prompt
    const checkShouldShow = () => {
      // Don't show if not supported
      if (!isPushSupported()) {
        return false;
      }
      
      // Don't show if already granted or denied
      const permission = getNotificationPermission();
      if (permission === 'granted' || permission === 'denied') {
        return false;
      }
      
      // Don't show if user has dismissed in last 7 days
      const dismissedAt = localStorage.getItem('push_prompt_dismissed');
      if (dismissedAt) {
        const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) {
          return false;
        }
      }
      
      return true;
    };
    
    // Delay showing the prompt for better UX
    const timer = setTimeout(() => {
      if (checkShouldShow()) {
        setVisible(true);
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleEnable = async () => {
    setStatus('requesting');
    
    const authToken = getToken();
    if (!authToken) {
      setStatus('error');
      setErrorMessage('Please log in to enable notifications');
      return;
    }
    
    const result = await setupPushNotifications(authToken);
    
    if (result.success) {
      setStatus('success');
      localStorage.removeItem('push_prompt_dismissed');
      
      // Auto-hide after success
      setTimeout(() => {
        setVisible(false);
        onDismiss?.();
      }, 2000);
    } else if (result.error === 'Permission denied') {
      setStatus('denied');
    } else {
      setStatus('error');
      setErrorMessage(result.error);
    }
  };
  
  const handleDismiss = () => {
    localStorage.setItem('push_prompt_dismissed', Date.now().toString());
    setVisible(false);
    onDismiss?.();
  };
  
  if (!visible) return null;
  
  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 animate-slide-up"
      data-testid="push-notification-prompt"
    >
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg p-4 text-white">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
          data-testid="push-prompt-close"
        >
          <X className="w-4 h-4" />
        </button>
        
        {status === 'success' ? (
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 flex-shrink-0" />
            <div>
              <p className="font-semibold">Notifications Enabled!</p>
              <p className="text-sm text-white/80">You'll get reminders to keep your streak going 🔥</p>
            </div>
          </div>
        ) : status === 'denied' ? (
          <div className="flex items-center gap-3">
            <BellOff className="w-8 h-8 flex-shrink-0" />
            <div>
              <p className="font-semibold">Notifications Blocked</p>
              <p className="text-sm text-white/80">
                Enable in browser settings to get reminders
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3 pr-6">
              <Bell className="w-8 h-8 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Stay on Track! 🔥</p>
                <p className="text-sm text-white/90 mt-1">
                  Get daily reminders to keep your savings streak alive and hit your goals.
                </p>
              </div>
            </div>
            
            {errorMessage && (
              <p className="text-xs text-red-200 mt-2 ml-11">{errorMessage}</p>
            )}
            
            <div className="flex gap-2 mt-4 ml-11">
              <button
                onClick={handleEnable}
                disabled={status === 'requesting'}
                className="flex-1 bg-white text-emerald-600 font-semibold py-2 px-4 rounded-lg hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="push-prompt-enable"
              >
                {status === 'requesting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Enabling...
                  </span>
                ) : (
                  'Enable Notifications'
                )}
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-white/80 hover:text-white transition-colors"
                data-testid="push-prompt-later"
              >
                Later
              </button>
            </div>
          </>
        )}
      </div>
      
      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
